import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { portalApi, type PortalMe } from "./api";

const PortalContext = createContext<{
  me: PortalMe | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
} | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<PortalMe | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      setMe(await portalApi<PortalMe>("/me"));
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    const clear = () => setMe(null);
    window.addEventListener("sailix-portal:unauthenticated", clear);
    return () => window.removeEventListener("sailix-portal:unauthenticated", clear);
  }, []);
  const logout = useCallback(async () => {
    await portalApi("/logout", { method: "POST" });
    setMe(null);
  }, []);
  const value = useMemo(() => ({ me, loading, refresh, logout }), [me, loading, refresh, logout]);
  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) throw new Error("usePortal must be used inside PortalProvider");
  return context;
}

export function RequirePortal() {
  const { me, loading } = usePortal();
  const location = useLocation();
  if (loading)
    return (
      <div className="app-loading" aria-live="polite">
        Opening your support portal…
      </div>
    );
  if (!me)
    return <Navigate to={`/portal/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <Outlet />;
}
